import React, { useEffect, useLayoutEffect, useState, useRef, useMemo, useId } from "react";
import { createPortal } from "react-dom";
import {
    X,
    Image,
    File,
    Send,
    Loader,
    Sparkles,
    Lock,
    Globe,
    Users,
    Calendar,
    Clock,
    ChevronDown,
    ThumbsUp,
    Heart,
    ThumbsDown,
    Bookmark,
    MessageCircle,
    Paperclip,
    Check,
    SlidersHorizontal
} from "lucide-react";
import { createPost } from "../api/posts";
import { fetchJoinedSpaces, type Space } from "../api/spaces";
import "./CreatePostModal.css";

type Props = {
    onClose: () => void;
    onPostCreated: (post: any) => void;
    initialMessage?: string;
    variant?: "modal" | "inline";
    initialShowAdvanced?: boolean;
};


const MAX_CHARS = 237;
const MAX_FILES = 10;
const MAX_FILE_MB = 10;
const ACCEPT = [
    "image/*",
    "video/*",
    "application/pdf",
    "text/plain",
].join(",");

// Advanced post options
const VISIBILITY_OPTIONS = [
    { value: "public", label: "Public", icon: Globe, description: "Visible to everyone" },
    { value: "space", label: "Space only", icon: Users, description: "Only members of this space" },
    { value: "private", label: "Private", icon: Lock, description: "Only you can see this" }
];

const SCHEDULE_OPTIONS = [
    { value: "now", label: "Post now", icon: Send },
    { value: "15min", label: "15 min", icon: Clock },
    { value: "1hour", label: "1 hour", icon: Clock },
    { value: "tomorrow", label: "Tomorrow", icon: Calendar }
];

// Individual reaction controls
const REACTION_OPTIONS = [
    { id: "like", label: "Likes", icon: ThumbsUp, color: "#67d9e8" },
    { id: "love", label: "Love", icon: Heart, color: "#f49abc" },
    { id: "dislike", label: "Dislikes", icon: ThumbsDown, color: "#ef969e" },
    { id: "save", label: "Saves", icon: Bookmark, color: "#78d5b6" },
    { id: "comment", label: "Comments", icon: MessageCircle, color: "#a3b8fa" }
];

export default function CreatePostModal({
    onClose,
    onPostCreated,
    initialMessage = "",
    variant = "modal",
    initialShowAdvanced = false,
}: Props) {
    const isInline = variant === "inline";
    const composerId = useId();
    const [message, setMessage] = useState(initialMessage.slice(0, MAX_CHARS));
    const [spaces, setSpaces] = useState<Space[]>([]);
    const [space, setSpace] = useState(() => localStorage.getItem("lastSpaceId") || "blackspace");
    const [error, setError] = useState("");
    const [files, setFiles] = useState<File[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [filesOpen, setFilesOpen] = useState(false);

    // Advanced options state
    const [showAdvanced, setShowAdvanced] = useState(initialShowAdvanced);
    const [visibility, setVisibility] = useState("public");
    const [schedule, setSchedule] = useState("now");
    const [isExclusive, setIsExclusive] = useState(false);

    // Individual reaction controls - all enabled by default
    const [enabledReactions, setEnabledReactions] = useState({
        like: true,
        love: true,
        dislike: true,
        save: true,
        comment: true
    });

    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const modalRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const submissionRef = useRef(false);
    const interactionRef = useRef({ canPost: false, submitting: false, onClose, onPost: () => {} });
    const [imagePreviews, setImagePreviews] = useState<{ file: File; url: string }[]>([]);

    const charCount = message.length;
    const overLimit = charCount > MAX_CHARS;
    const canPost = !submitting && !overLimit && (message.trim().length > 0 || files.length > 0);

    useEffect(() => {
        setMessage(initialMessage.slice(0, MAX_CHARS));
    }, [initialMessage]);

    useLayoutEffect(() => {
        const textarea = textareaRef.current;
        if (!textarea) return;
        textarea.style.height = "auto";
        textarea.style.height = `${Math.min(textarea.scrollHeight, 240)}px`;
        textarea.style.overflowY = textarea.scrollHeight > 240 ? "auto" : "hidden";
    }, [message]);

    // Focus once on entry; changing a setting must not pull focus back to the editor.
    useEffect(() => {
        const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        const previousOverflow = document.body.style.overflow;
        if (!isInline) document.body.style.overflow = "hidden";
        const focusFrame = window.requestAnimationFrame(() => textareaRef.current?.focus({ preventScroll: isInline }));
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.isComposing) return;
            const current = interactionRef.current;
            if (event.key === "Escape") {
                event.preventDefault();
                event.stopPropagation();
                if (!current.submitting) current.onClose();
            }
            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "enter" && current.canPost &&
                (!isInline || modalRef.current?.contains(event.target as Node))) {
                event.preventDefault();
                current.onPost();
            }
            if (isInline || event.key !== "Tab") return;
            const controls = Array.from(modalRef.current?.querySelectorAll<HTMLElement>(
                'button:not([disabled]), select:not([disabled]), textarea:not([disabled]), input:not([type="file"])'
            ) || []).filter((control) => !control.matches(":disabled") && control.getClientRects().length > 0);
            const first = controls[0];
            const last = controls[controls.length - 1];
            if (!first || !last) return;
            const outside = !modalRef.current?.contains(document.activeElement);
            if (event.shiftKey && (document.activeElement === first || outside)) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && (document.activeElement === last || outside)) {
                event.preventDefault();
                first.focus();
            }
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => {
            window.cancelAnimationFrame(focusFrame);
            document.removeEventListener("keydown", handleKeyDown);
            if (!isInline) document.body.style.overflow = previousOverflow;
            if (previousFocus?.isConnected) previousFocus.focus({ preventScroll: true });
        };
    }, [isInline]);

    useEffect(() => {
        let alive = true;
        fetchJoinedSpaces({ limit: 100 })
            .then((data) => {
                if (!alive) return;
                const loaded = data.spaces.length
                    ? data.spaces
                    : [{
                        id: "blackspace",
                        title: "Blackspace",
                        desc: "",
                        by: "Blackspace",
                        members: "0",
                        online: "0",
                        category: "General",
                        isNew: false,
                        isJoined: true,
                        created: "",
                    }];
                setSpaces(loaded);
                if (!loaded.some((item) => item.id === space)) {
                    setSpace(loaded[0].id);
                }
            })
            .catch(() => {
                const fallback = {
                    id: "blackspace",
                    title: "Blackspace",
                    desc: "",
                    by: "Blackspace",
                    members: "0",
                    online: "0",
                    category: "General",
                    isNew: false,
                    isJoined: true,
                    created: "",
                };
                if (alive) {
                    setSpaces([fallback]);
                    setSpace("blackspace");
                }
            });

        return () => {
            alive = false;
        };
    }, [space]);

    const selectedSpace = spaces.find((item) => item.id === space) || spaces[0];

    // Remember last used space
    useEffect(() => {
        localStorage.setItem("lastSpaceId", space);
        if (selectedSpace?.title) {
            localStorage.setItem("lastSpace", selectedSpace.title);
        }
    }, [space, selectedSpace?.title]);

    const addFiles = (incoming: File[]) => {
        const current = [...files];
        const availableSlots = Math.max(0, MAX_FILES - current.length);
        const sliced = incoming.slice(0, availableSlots);

        const errors: string[] = [];
        const valid: File[] = [];

        for (const file of sliced) {
            const sizeMB = file.size / (1024 * 1024);
            if (sizeMB > MAX_FILE_MB) {
                errors.push(`${file.name} is larger than ${MAX_FILE_MB} MB`);
                continue;
            }
            valid.push(file);
        }

        if (errors.length) setError(errors.join(" - "));
        if (valid.length) setFiles((prev) => [...prev, ...valid]);
    };

    const handleDrop = (e: React.DragEvent<HTMLElement>) => {
        e.preventDefault();
        setIsDragging(false);
        if (e.dataTransfer?.files) addFiles(Array.from(e.dataTransfer.files));
    };

    const handleDragOver = (e: React.DragEvent<HTMLElement>) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = () => {
        setIsDragging(false);
    };

    const handleBrowse = () => {
        fileInputRef.current?.click();
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) addFiles(Array.from(e.target.files));
        e.target.value = ""; // Reset input
    };

    const removeFile = (index: number) => {
        setFiles((prev) => prev.filter((_, i) => i !== index));
    };

    const handlePost = async () => {
        return handleLivePost();
    };

    const handleLivePost = async () => {
        if (!canPost || submissionRef.current) return;
        submissionRef.current = true;
        setSubmitting(true);
        setError("");

        try {
            const created = await createPost({
                content: message.trim(),
                spaceId: selectedSpace?.id || space,
                spaceName: selectedSpace?.title || "Blackspace",
                allowedActions: Object.entries(enabledReactions)
                    .filter(([, enabled]) => enabled)
                    .map(([action]) => action),
                visibility,
                schedule,
                prompt: initialMessage.includes("#daily-prompt") ? initialMessage : "",
                isExclusive,
            });
            onPostCreated(created);
            onClose();
        } catch (err: any) {
            console.error("CREATE POST CRASH:", err);
            setError(err.message || "Something went wrong");
        } finally {
            submissionRef.current = false;
            setSubmitting(false);
        }
    };

    const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget && !submissionRef.current) onClose();
    };

    const imageFiles = useMemo(() => files.filter((f) => f.type.startsWith("image/")), [files]);
    const otherFiles = useMemo(() => files.filter((f) => !f.type.startsWith("image/")), [files]);
    useEffect(() => {
        const previews = imageFiles.map((file) => ({ file, url: URL.createObjectURL(file) }));
        setImagePreviews(previews);
        return () => previews.forEach(({ url }) => URL.revokeObjectURL(url));
    }, [imageFiles]);
    const fileSummary = files.length > 0
        ? `${files.length} ${files.length === 1 ? "file" : "files"} ready`
        : "Optional";

    const selectedVisibility = VISIBILITY_OPTIONS.find((option) => option.value === visibility)!;
    const selectedSchedule = SCHEDULE_OPTIONS.find((option) => option.value === schedule)!;
    const VisibilityIcon = selectedVisibility.icon;
    interactionRef.current = { canPost, submitting, onClose, onPost: handlePost };

    // Toggle individual reaction
    const toggleReaction = (reactionId: string) => {
        setEnabledReactions(prev => ({
            ...prev,
            [reactionId]: !prev[reactionId as keyof typeof prev]
        }));
    };

    const panel = (
        <div
            ref={modalRef}
            className={"bs-post-composer " + (isInline ? "is-inline" : "is-modal")}
            role={isInline ? "region" : "dialog"}
            aria-modal={isInline ? undefined : true}
            aria-labelledby={composerId + "-title"}
            aria-busy={submitting}
        >
            <header className="bs-post-composer-header">
                <span className="bs-post-composer-emblem"><Sparkles size={19} aria-hidden="true" /></span>
                <div className="bs-post-composer-heading">
                    <h2 id={composerId + "-title"}>{isInline ? "Advanced composer" : "Create a post"}</h2>
                    <p>{isInline ? "Full controls without leaving the feed" : "Share your thoughts with the community"}</p>
                </div>
                <button type="button" onClick={onClose} disabled={submitting} className="bs-post-composer-close" aria-label="Close composer" title="Close composer">
                    <X size={18} aria-hidden="true" />
                </button>
            </header>

            <div className="bs-post-composer-body">
                <fieldset disabled={submitting} className="bs-post-composer-fields">
                    <div className="bs-post-composer-destination">
                        <label htmlFor={composerId + "-space"}>Post to</label>
                        <div className="bs-post-composer-select">
                            <Users size={16} aria-hidden="true" />
                            <select id={composerId + "-space"} value={space} onChange={(event) => setSpace(event.target.value)}>
                                {!spaces.length && <option value={space}>Blackspace</option>}
                                {spaces.map((item) => <option key={item.id} value={item.id}>{item.title}</option>)}
                            </select>
                            <ChevronDown size={15} aria-hidden="true" />
                        </div>
                    </div>

                    <div className="bs-post-composer-message">
                        <div className="bs-post-composer-label-row">
                            <label htmlFor={composerId + "-message"}>Your message</label>
                            <span id={composerId + "-count"} className={"bs-post-composer-count " + (charCount >= MAX_CHARS - 20 ? "is-near-limit" : "")}>
                                {charCount}<span aria-hidden="true"> / </span><span className="bs-post-composer-sr-only"> of </span>{MAX_CHARS}
                                <span className="bs-post-composer-sr-only"> characters</span>
                            </span>
                        </div>
                        <textarea
                            id={composerId + "-message"}
                            ref={textareaRef}
                            rows={3}
                            placeholder="What's happening?"
                            value={message}
                            onChange={(event) => {
                                setMessage(event.target.value.slice(0, MAX_CHARS));
                                if (error) setError("");
                            }}
                            maxLength={MAX_CHARS}
                            aria-describedby={composerId + "-count"}
                        />
                    </div>

                    <div className="bs-post-composer-toolbar">
                        <fieldset className="bs-post-composer-interactions">
                            <legend>Allow interactions</legend>
                            <div>
                                {REACTION_OPTIONS.map((reaction) => {
                                    const enabled = enabledReactions[reaction.id as keyof typeof enabledReactions];
                                    const Icon = reaction.icon;
                                    const label = reaction.label + (enabled ? " enabled" : " disabled");
                                    return (
                                        <button
                                            key={reaction.id}
                                            type="button"
                                            onClick={() => toggleReaction(reaction.id)}
                                            className="bs-post-composer-reaction"
                                            style={{ "--reaction-color": reaction.color } as React.CSSProperties}
                                            aria-pressed={enabled}
                                            aria-label={label}
                                            title={label}
                                        >
                                            <Icon size={17} aria-hidden="true" />
                                            <span className="bs-post-composer-tooltip" aria-hidden="true">{label}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </fieldset>
                        <button
                            type="button"
                            className="bs-post-composer-settings-toggle"
                            aria-label="Post settings"
                            title="Post settings"
                            onClick={() => setShowAdvanced((current) => !current)}
                            aria-expanded={showAdvanced}
                            aria-controls={composerId + "-settings"}
                        >
                            <SlidersHorizontal size={15} aria-hidden="true" />
                            <span>Post settings</span>
                            <ChevronDown size={14} aria-hidden="true" />
                        </button>
                    </div>

                    {showAdvanced && (
                        <div id={composerId + "-settings"} className="bs-post-composer-settings">
                            <div className="bs-post-composer-settings-grid">
                                <fieldset className="bs-post-composer-option-group">
                                    <legend>Visibility</legend>
                                    <div className="bs-post-composer-visibility">
                                        {VISIBILITY_OPTIONS.map((option) => {
                                            const Icon = option.icon;
                                            return (
                                                <button key={option.value} type="button" onClick={() => setVisibility(option.value)} aria-pressed={visibility === option.value} aria-label={option.label + ": " + option.description} className="bs-post-composer-option">
                                                    <Icon size={15} aria-hidden="true" /><span className="bs-post-composer-option-label">{option.label}</span>
                                                    {visibility === option.value && <Check size={13} className="bs-post-composer-choice-check" aria-hidden="true" />}
                                                    <span className="bs-post-composer-tooltip" aria-hidden="true">{option.label} · {option.description}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                    <p className="bs-post-composer-option-description">{selectedVisibility.description}</p>
                                </fieldset>
                                <fieldset className="bs-post-composer-option-group">
                                    <legend>Publish</legend>
                                    <div className="bs-post-composer-schedule">
                                        {SCHEDULE_OPTIONS.map((option) => {
                                            const Icon = option.icon;
                                            return (
                                                <button key={option.value} type="button" onClick={() => setSchedule(option.value)} aria-pressed={schedule === option.value} aria-label={option.label} className="bs-post-composer-option">
                                                    <Icon size={15} aria-hidden="true" /><span className="bs-post-composer-option-label">{option.label}</span>
                                                    {schedule === option.value && <Check size={13} className="bs-post-composer-choice-check" aria-hidden="true" />}
                                                    <span className="bs-post-composer-tooltip" aria-hidden="true">{option.label}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </fieldset>
                            </div>
                            <label className="bs-post-composer-exclusive">
                                <Sparkles size={16} aria-hidden="true" />
                                <span>Exclusive content</span>
                                <input type="checkbox" checked={isExclusive} onChange={(event) => setIsExclusive(event.target.checked)} />
                                <span className="bs-post-composer-switch" aria-hidden="true" />
                            </label>
                        </div>
                    )}

                    <section className="bs-post-composer-files" aria-label="Attachments">
                        <button type="button" onClick={() => setFilesOpen((current) => !current)} className="bs-post-composer-files-toggle" aria-expanded={filesOpen || files.length > 0} aria-controls={composerId + "-files"}>
                            <Paperclip size={17} aria-hidden="true" />
                            <span>Files</span>
                            <small>{fileSummary}</small>
                            <ChevronDown size={15} aria-hidden="true" />
                        </button>
                        <input ref={fileInputRef} type="file" multiple accept={ACCEPT} onChange={handleFileSelect} className="bs-post-composer-file-input" aria-label="Attach files" />
                        {(filesOpen || files.length > 0) && (
                            <div id={composerId + "-files"} className="bs-post-composer-file-content">
                                <button
                                    type="button"
                                    onDrop={handleDrop}
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onClick={handleBrowse}
                                    className={"bs-post-composer-dropzone " + (isDragging ? "is-dragging" : "")}
                                >
                                    <Image size={20} aria-hidden="true" />
                                    <span><strong>Add files</strong><small>Images, videos, PDFs or text · {MAX_FILE_MB} MB each · Up to {MAX_FILES}</small></span>
                                </button>
                                {imagePreviews.length > 0 && (
                                    <div className="bs-post-composer-image-grid">
                                        {imagePreviews.map(({ file, url }) => (
                                            <div key={url} className="bs-post-composer-image">
                                                <img src={url} alt={file.name} />
                                                <button type="button" onClick={() => removeFile(files.indexOf(file))} aria-label={"Remove " + file.name} title={"Remove " + file.name}><X size={14} aria-hidden="true" /></button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {otherFiles.map((file, index) => (
                                    <div key={file.name + "-" + index} className="bs-post-composer-file">
                                        <File size={16} aria-hidden="true" />
                                        <span title={file.name}>{file.name}</span>
                                        <button type="button" onClick={() => removeFile(files.indexOf(file))} aria-label={"Remove " + file.name} title={"Remove " + file.name}><X size={16} aria-hidden="true" /></button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>
                </fieldset>
                {error && <p className="bs-post-composer-error" role="alert">{error}</p>}
            </div>

            <footer className="bs-post-composer-footer">
                <div className="bs-post-composer-status" aria-label={selectedVisibility.label + ", " + selectedSchedule.label}>
                    <VisibilityIcon size={14} aria-hidden="true" />
                    <span>{selectedVisibility.label}</span>
                    <span className="bs-post-composer-status-separator" aria-hidden="true">·</span>
                    <span>{selectedSchedule.label}</span>
                </div>
                <div className="bs-post-composer-actions">
                    <button type="button" onClick={onClose} disabled={submitting} className="bs-post-composer-cancel">Cancel</button>
                    <button type="button" onClick={handlePost} disabled={!canPost} className="bs-post-composer-submit">
                        {submitting ? <Loader size={16} className="bs-post-composer-spinner" aria-hidden="true" /> : schedule === "now" ? <Send size={15} aria-hidden="true" /> : <Calendar size={15} aria-hidden="true" />}
                        {submitting ? "Posting…" : schedule === "now" ? "Post" : "Schedule"}
                    </button>
                </div>
            </footer>
        </div>
    );

    if (isInline) return panel;
    const modal = <div className="bs-post-composer-overlay" onMouseDown={handleBackdropClick}>{panel}</div>;
    return typeof document === "undefined" ? modal : createPortal(modal, document.body);
}
